import { ReactNode, MouseEvent, ElementType, CSSProperties } from 'react';
import { Variant } from '@material-ui/core/styles/createTypography';
import { ColumnDef, ExpandedState, SortingState } from '@tanstack/react-table';

export type { SortingState };

export type { ExpandedState };

export interface WbTableLoader {
  loader?: JSX.Element;
  loading: boolean;
  columnCount?: number;
  rowCount?: number;
}
export interface WbTableComponents<T> {
  /**
   * @deprecated Configure the table using the columns field instead
   */
  tableHeader?: JSX.Element;
  tableLoader?: WbTableLoader;
  tableContent?: {
    /**
     * @deprecated Configure the table using the columns field instead
     */
    body?: JSX.Element;
  } & Partial<PublicTableContentProps<T>>;
}

export type RenderRowDetail<T> = (row: T) => ReactNode;

export interface WbTableStyles {
  container?: CSSProperties;
  header?: CSSProperties;
  body?: CSSProperties;
}
export type WbTableSortDirection = 'asc' | 'desc' | undefined;
export type WbTableSort<T> = (
  event: MouseEvent<Element>,
  cell: TableCellProps<T>,
  direction: WbTableSortDirection,
) => void;

export interface WbTableProps<T> {
  components?: WbTableComponents<T>;
  noDataLabel?: string;
  noDataComponent?: JSX.Element;
  pagination?: ConditionalPaginationProps;
  styles?: WbTableStyles;
  classNames?: {
    header?: string;
    container?: string;
    body?: string;
  };
  pinActionsColumnOnMobile?: boolean;
  /**
   * Allows to specify width: "auto" for some columns, so that their width will be dynamically computed to fit their content
   * Compared to the default "fixed" layout, in this mode columns with no defined widths won't share the remaining space equally
   * but their width will be computed by the browser auto layout algorithm in a less predictable way. Use this when having a column width dynamically fitting its content is strictly required.
   * Ideally this will disappear and "auto" be allowed in the standard default mode with no other behaviour changes
   */
  enableAutoLayout?: boolean;
  sorting?: SortingState;
  onSortingChange?: React.Dispatch<React.SetStateAction<SortingState>>;
  manualSorting?: boolean;
  stickyHeader?: boolean;
  /**
   * Control the state determning which rows currently are showing their detail
   */
  expandedRowDetail?: ExpandedState;
  /**
   * Control the state determning which rows currently are showing their detail
   */
  onExpandedRowDetailChange?: React.Dispatch<
    React.SetStateAction<ExpandedState>
  >;
  /**
   * If defined, rows can be expanded/collapsed and show a special detail row rendering the return value of this function
   */
  renderRowDetail?: RenderRowDetail<T>;
  selection?: 'single' | 'multiple';
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  checkIfDisabled?: (row: T) => boolean;
  tooltipIfDisabled?: string;
  tableStyles?: CSSProperties;
  columnPinning?: { left?: string[]; right?: string[] };
}

export type ConditionalPaginationProps = {
  rowsPerPageOptions?: Array<number>;
  countlessOptions?: { hasNextPage: () => boolean; isFirstPage: () => boolean };
  count: number;
  limit: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
} & (
  | {
      currentPage: number;
    }
  | {
      offset: number;
    }
);

export enum WbColumnWidthType {
  FIXED = 'FIXED',
  PERCENT = 'PERCENT',
  AUTO = 'AUTO',
  NOT_DEFINED = 'NOT_DEFINED',
}

type WbColumnWidthFixed = number | `${number}` | `${number}px`;

export type WbColumnWidth =
  | WbColumnWidthFixed
  | `${number}%`
  | 'auto'
  | undefined;

export type ParsedWbColumnWidth =
  | { type: WbColumnWidthType.FIXED; value: number }
  | { type: WbColumnWidthType.PERCENT; value: number }
  | { type: WbColumnWidthType.AUTO }
  | { type: WbColumnWidthType.NOT_DEFINED };

export type WbColumnMinMaxWidth = WbColumnWidthFixed | undefined;

export type TableCellProps<T> = {
  minWidth?: WbColumnMinMaxWidth;
  maxWidth?: WbColumnMinMaxWidth;
  /**
   * Selecting every value but "auto" uses the default fixed layout (faster).
   * Choosing auto makes the column width equal to the column content but makes table switch to auto layout
   */
  width?: WbColumnWidth;
  /**
   * This is only has effect when choosing width: "auto", determines a minimum width for the auto sized element
   */
  autoMinWidth?: WbColumnMinMaxWidth;
  /**
   * This is only has effect when choosing width: "auto", determines a maximum width for the auto sized element
   */
  autoMaxWidth?: WbColumnMinMaxWidth;
  resizable?: boolean;
  uniqueFieldId?: string;
  typographyProps?: {
    color?:
      | 'inherit'
      | 'initial'
      | 'secondary'
      | 'primary'
      | 'textPrimary'
      | 'textSecondary'
      | 'error';
    component?: ElementType;
    variant?: Variant | 'srOnly' | 'inherit';
    style?: CSSProperties;
    className?: string;
  };
  tooltip?: boolean;
  link?: string;
  field?: keyof T;
  headerName?: string;
  headerTooltip?: string;
  headerDescription?: JSX.Element;
  fieldRender?: (
    field: T,
    extraInfo: { rowDepth: number },
  ) => ReactNode | JSX.Element;
  formatter?: (value: any) => any;
  sortable?: boolean;
};

// The props directly exposed under 'components.tableContent' prop of WbTable
export interface PublicTableContentProps<T> {
  columns?: Array<TableCellProps<T>>;
  rows?: Array<T>;
  rowsWrapper?: any;
  treeProps?: {
    getRowChildren: (row: T) => T[];

    // for controlled mode
    expandedRowsIds: string[];
    onExpandChange: (newExpandedIds: string[]) => void;
    /** Called when a node is expanded. Used for async child loading. */
    onExpand?: (rowId: string) => void;
    /** Row IDs currently loading children. Used to identify spinner sentinel rows. */
    loadingRowsIds?: string[];
    /**
     * If set, identifies a row as a "load more" sentinel. Return the parent node's
     * ID if this row is a load-more trigger, or undefined otherwise.
     */
    getLoadMoreParentId?: (row: T) => string | undefined;
    /**
     * If set, identifies a row as a "loading" sentinel. Return the parent node's
     * ID if this row represents in-progress child loading, or undefined otherwise.
     */
    getLoadingParentId?: (row: T) => string | undefined;
    /** Called when the user clicks "Load more" for a given parent node. */
    onLoadMoreChildren?: (parentId: string) => void;
    /**
     * When provided, used instead of `getRowChildren(row).length > 0` to decide
     * whether to render an expand toggle. Useful for lazy-loaded trees where
     * children are not yet in memory but the node is known to have some.
     */
    hasChildren?: (row: T) => boolean;
  };
  getRowId?: (row: T) => string;
  rowDynamicClassName?: (row: T) => string | undefined;
  onRowClick?: (
    element: T,
    event: MouseEvent<HTMLTableRowElement, MouseEvent>,
    index: number,
  ) => void;
  disableRowHighlightOnHover?: boolean;
}

export interface TableContentProps<T> extends PublicTableContentProps<T> {
  rowStyle?: CSSProperties;
  rowClassName?: string;
}

// Extend TanStack column definition
export type CustomColumnDef<T> = ColumnDef<T> & {
  originalWidth?: WbColumnWidth;
  id: string;
  parsedWidth: ParsedWbColumnWidth;
  parsedMinWidth: number;
  parsedMaxWidth: number;
  parsedAutoMinWidth: number | undefined;
  parsedAutoMaxWidth: number | undefined;
};

export type TableSelectionProps<T = unknown> = {
  selectedItems: T[];
  clearSelection: () => void;
};
